/* AssistantConversation — the reusable Ask Revvly chat surface (empty state,
   message thread, and docked composer). Shared by the full-page assistant and
   the onboarding product-setup step, so it owns its own layout dims (--ac-*)
   rather than borrowing the home page's. Canonical --rv-* tokens only. */

.Conversation {
  --ac-max-width: 48rem;
  --ac-empty-width: 40rem;
  --ac-empty-text-width: 30rem;
  --ac-composer-max-height: 40vh;

  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.Scroll {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: var(--rv-space-4) var(--rv-space-6);
}

/* ---------------- empty state ---------------- */

.Empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--rv-space-3);
  width: 100%;
  max-width: var(--ac-empty-width);
  margin: 0 auto;
  padding-top: min(10vh, 6rem);
  text-align: center;
}

.EmptyMark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--rv-space-12);
  height: var(--rv-space-12);
  margin-bottom: var(--rv-space-2);
  background: var(--rv-accent-glow-12);
  color: var(--rv-accent);
  border-radius: var(--rv-radius-2xl);
  box-shadow: inset 0 0 0 1px var(--rv-accent-glow-20);
}

.EmptyMark svg {
  width: var(--rv-icon-lg);
  height: var(--rv-icon-lg);
}

.EmptyTitle {
  margin: 0;
  font-family: var(--rv-font-display);
  font-size: var(--rv-text-2xl);
  font-weight: var(--rv-w-medium);
  letter-spacing: var(--rv-track-tight);
  color: var(--rv-fg-1);
}

.EmptySubtitle {
  margin: 0;
  max-width: var(--ac-empty-text-width);
  font-size: var(--rv-text-md);
  line-height: var(--line-height-lg);
  color: var(--rv-fg-3);
}

.Suggestions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rv-space-3);
  width: 100%;
  margin-top: var(--rv-space-5);
  text-align: left;
}

.SuggestionCard {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--rv-space-3);
  height: auto;
  min-height: 0;
  padding: var(--rv-space-4);
  border-radius: var(--rv-radius-xl);
  white-space: normal;
  text-align: left;
}

.SuggestionIcon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: var(--rv-space-8);
  height: var(--rv-space-8);

  /* --rv-neutral-* stays light in this app's dark mode, so use a mode-aware
     surface token for the chip fill (else the tile hides the icon). */
  background: var(--rv-bg-app-preview);
  color: var(--rv-fg-2);
  border-radius: var(--rv-radius-md);
}

.SuggestionIcon svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
}

.SuggestionText {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  min-width: 0;
}

.SuggestionTitle {
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-medium);
  color: var(--rv-fg-1);
}

.SuggestionSubtitle {
  font-size: var(--rv-text-xs);
  line-height: var(--line-height-md);
  color: var(--rv-fg-3);
}

/* ---------------- conversation thread ---------------- */

/* The top auto margin pins a short thread to the bottom of the scroll area,
   next to the composer, the way chat apps do; once the thread overflows, the
   margin collapses to 0 and the area scrolls normally. */
.Thread {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-5);
  width: 100%;
  max-width: var(--ac-max-width);
  margin: auto auto 0;
  padding: var(--rv-space-2) 0 var(--rv-space-4);
}

.UserTurn {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.AiTurn {
  display: flex;
  align-items: flex-start;
  gap: var(--rv-space-3);
}

.AiAvatar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: var(--rv-space-8);
  height: var(--rv-space-8);
  margin-top: var(--space-xxs);
  background: var(--rv-accent-glow-12);
  color: var(--rv-accent);
  border-radius: var(--rv-radius-md);
  box-shadow: inset 0 0 0 1px var(--rv-accent-glow-20);
}

.AiAvatar svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
}

.AiBody {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--rv-space-4);
  min-width: 0;
  padding-top: var(--space-xxs);
}

.ToolActivity {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

.ToolChip {
  display: inline-flex;
  align-items: center;
  gap: var(--rv-space-1);
  padding: var(--rv-space-1) var(--rv-space-3);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-full);
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-3);
  font-size: var(--rv-text-xs);
}

/* Marks an answer that used the workspace's own docs, so it is never mistaken for Revvly's. */
.ToolChipDocs {
  border-color: var(--mint-alpha-45);
  background: var(--mint-alpha-10);
  color: var(--rv-fg-1);
}

.ToolChip svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.MessageBody {
  max-width: 100%;
  color: var(--rv-fg-1);
  font-size: var(--rv-text-md);
  line-height: var(--line-height-lg);
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.MessageUser {
  max-width: 85%;
  padding: var(--rv-space-3) var(--rv-space-4);
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-2xl);
  border-bottom-right-radius: var(--rv-radius-sm);
  color: var(--rv-fg-1);
  font-size: var(--rv-text-md);
  line-height: var(--line-height-lg);
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

/* Rendered markdown (assistant replies). Block elements own their spacing, so
   MessageBody's pre-wrap is reset here. Canonical --rv-* tokens only. */
.Markdown {
  white-space: normal;
}

.Markdown > :first-child {
  margin-top: 0;
}

.Markdown > :last-child {
  margin-bottom: 0;
}

.Markdown p {
  margin: 0 0 var(--rv-space-3);
}

/* The global reset strips list markers, so restore them for replies. */
.Markdown ul,
.Markdown ol {
  margin: 0 0 var(--rv-space-3);
  padding-left: var(--rv-space-6);
}

.Markdown ul {
  list-style: disc;
}

.Markdown ol {
  list-style: decimal;
}

.Markdown li > ul {
  list-style: circle;
}

.Markdown li {
  margin: var(--rv-space-1) 0;
}

.Markdown li > ul,
.Markdown li > ol {
  margin-bottom: 0;
}

.Markdown h1,
.Markdown h2,
.Markdown h3,
.Markdown h4,
.Markdown h5,
.Markdown h6 {
  margin: var(--rv-space-4) 0 var(--rv-space-2);
  font-weight: var(--rv-w-semibold);
  line-height: var(--line-height-md);
}

.Markdown h1 {
  font-size: var(--rv-text-xl);
}

.Markdown h2 {
  font-size: var(--rv-text-lg);
}

.Markdown h3,
.Markdown h4,
.Markdown h5,
.Markdown h6 {
  font-size: var(--rv-text-md);
}

.Markdown a {
  color: var(--rv-accent);
  text-decoration: underline;
}

.Markdown strong {
  font-weight: var(--rv-w-semibold);
}

.Markdown code {
  padding: 0 var(--rv-space-1);
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-sm);
  background: var(--rv-bg-app-preview);
  border-radius: var(--rv-radius-sm);
}

.Markdown pre {
  margin: 0 0 var(--rv-space-3);
  padding: var(--rv-space-3);
  overflow-x: auto;
  background: var(--rv-bg-app-preview);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-md);
}

.Markdown pre code {
  padding: 0;
  font-size: var(--rv-text-sm);
  background: transparent;
  border-radius: 0;
}

.Markdown blockquote {
  margin: 0 0 var(--rv-space-3);
  padding-left: var(--rv-space-4);
  border-left: 1px solid var(--rv-border);
  color: var(--rv-fg-2);
}

/* Scroll container gives markdown tables a bordered, rounded frame (same look as
   the structured table blocks); the table draws its own hairlines inside. */
.TableScroll {
  margin: 0 0 var(--rv-space-3);
  overflow-x: auto;
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-lg);
  background: var(--rv-surface);
}

.Markdown table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--rv-text-sm);
}

.Markdown th {
  padding: var(--rv-space-2) var(--rv-space-3);
  text-align: left;
  font-family: var(--rv-font-sans);
  font-weight: var(--rv-w-semibold);
  font-size: var(--rv-text-xxs);
  letter-spacing: var(--rv-track-wide);
  text-transform: uppercase;
  color: var(--rv-fg-3);
  background: var(--rv-bg-app-subtle);
  border-bottom: 1px solid var(--rv-border);
  white-space: nowrap;
}

.Markdown td {
  padding: var(--rv-space-2) var(--rv-space-3);
  border-bottom: 1px solid var(--rv-border-subtle);
  color: var(--rv-fg-1);
  font-variant-numeric: tabular-nums;
  vertical-align: top;
}

/* Divider between the first (label) column and the rest for scannability. */
.Markdown th:not(:last-child),
.Markdown td:not(:last-child) {
  border-right: 1px solid var(--rv-border-subtle);
}

.Markdown tr:last-child td {
  border-bottom: none;
}

.MessageError {
  color: var(--rv-danger-ink);
}

/* Working indicator: three dots that blink in sequence while the agent runs. */
.Dots {
  display: inline-flex;
  gap: var(--rv-space-1);
  align-items: center;
  padding: var(--rv-space-1) 0;
}

.Dot {
  width: var(--rv-space-1);
  height: var(--rv-space-1);
  border-radius: var(--rv-radius-full);
  background: var(--rv-fg-3);
  animation: ac-dot-blink 1.4s var(--rv-ease-in-out) infinite both;
}

.Dot:nth-child(2) {
  animation-delay: 0.2s;
}

.Dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes ac-dot-blink {
  0%,
  80%,
  100% {
    opacity: 25%;
  }

  40% {
    opacity: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .Dot {
    animation: none;
    opacity: 60%;
  }
}

/* ---------------- composer ---------------- */

.ComposerDock {
  padding: var(--rv-space-2) var(--rv-space-6) var(--rv-space-2);
}

.ComposerAccessory {
  width: 100%;
  max-width: var(--ac-max-width);
  margin: 0 auto;
}

/* The accessory may render nothing (no Support): then it takes no room. */
.ComposerAccessory:not(:empty) {
  margin-bottom: var(--rv-space-2);
}

/* "Copy" under a settled answer, to paste it to a customer in any channel. */
.AnswerActions {
  display: flex;
  margin-top: calc(var(--rv-space-1) * -1);
}

.CopyButton {
  display: inline-flex;
  align-items: center;
  gap: var(--rv-space-1);
  color: var(--rv-fg-3);
}

.CopyButton svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.Composer {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  width: 100%;
  max-width: var(--ac-max-width);
  margin: 0 auto;
  padding: var(--rv-space-1);
  background: var(--rv-surface);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-2xl);
  box-shadow: var(--rv-shadow-sm);
}

.Composer:focus-within,
.ComposerDragging {
  border-color: var(--rv-accent);
  box-shadow: var(--rv-shadow-ring-focus);
}

.ComposerRow {
  display: flex;
  gap: var(--rv-space-1);
  align-items: flex-end;
}

.Composer .Input[data-slot='textarea'] {
  flex: 1;
  min-width: 0;

  /* Grows with its content (height is set imperatively on input) up to this
     cap, then scrolls — so long drafts stay readable while typing. */
  max-height: var(--ac-composer-max-height);
  height: auto;
  min-height: 0;
  padding: var(--rv-space-1) 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: var(--rv-fg-1);
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-md);
  line-height: var(--line-height-lg);
  resize: none;
  overflow-y: auto;
  box-shadow: none;
}

.Composer .Input[data-slot='textarea']:hover,
.Composer .Input[data-slot='textarea']:focus,
.Composer .Input[data-slot='textarea']:focus-visible {
  outline: none;
  border: none;
  box-shadow: none;
}

.Composer .Input::placeholder {
  color: var(--rv-fg-4);
}

.Send {
  flex: 0 0 auto;
  width: var(--rv-space-8);
  height: var(--rv-space-8);
  padding: 0;
  border-radius: var(--rv-radius-lg);
  background: transparent;
  color: var(--rv-fg-3);
}

.Send:hover:not(:disabled) {
  background: transparent;
  color: var(--rv-fg-1);
}

.Send svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
}

/* ---------------- proposed-action confirmation card ---------------- */

.ProposalCard {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-3);
  padding: var(--rv-space-4);
  background: var(--rv-surface);
  border: 1px solid var(--rv-accent);
  border-radius: var(--rv-radius-2xl);
  box-shadow:
    0 0 0 1px var(--rv-accent-glow-12),
    var(--rv-shadow-sm);
}

.ProposalSummary {
  margin: 0;
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.ProposalDetails {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-1);
  margin: 0;
}

.ProposalDetailRow {
  display: flex;
  gap: var(--rv-space-3);
}

.ProposalDetailKey {
  min-width: var(--rv-space-20);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ProposalDetailValue {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-1);
  overflow-wrap: anywhere;
}

.ProposalNestedList {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  margin: 0;
  padding-left: var(--rv-space-5);
  list-style: disc;
}

.ProposalNestedItem {
  padding-left: var(--rv-space-1);
}

.ProposalNestedDetails {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-1);
  margin: 0;
}

.ProposalNestedRow {
  display: flex;
  gap: var(--rv-space-2);
}

.ProposalNestedKey {
  min-width: var(--rv-space-16);
  color: var(--rv-fg-3);
}

.ProposalNestedValue {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--rv-fg-1);
  overflow-wrap: anywhere;
}

.ProposalActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

.DeclineReason {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
}

.DeclineReasonLabel {
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ProposalStatus {
  margin: 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ProposalStatusError {
  color: var(--rv-danger-ink);
}

.ProposalStatusUncertain {
  color: var(--rv-warning-ink);
}

@media (--max-md) {
  .Suggestions {
    grid-template-columns: 1fr;
  }
}

/* ---------------- composer attachments ---------------- */

.AttachInput {
  display: none;
}

.AttachButton {
  flex: 0 0 auto;
  width: var(--rv-space-8);
  height: var(--rv-space-8);
  padding: 0;
  border-radius: var(--rv-radius-lg);
  color: var(--rv-fg-3);
}

.AttachButton svg {
  width: var(--rv-icon-md);
  height: var(--rv-icon-md);
}

.Attachments {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
  padding: var(--rv-space-1) var(--rv-space-1) 0;
}

.AttachmentChip {
  display: inline-flex;
  align-items: center;
  gap: var(--rv-space-2);
  min-width: 0;
  max-width: 100%;
  padding: var(--rv-space-1) var(--rv-space-1) var(--rv-space-1) var(--rv-space-3);
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-2);
}

.AttachmentChip > svg {
  flex: 0 0 auto;
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
  color: var(--rv-fg-3);
}

.AttachmentChipError {
  background: var(--rv-danger-bg);
  border-color: var(--rv-danger);
  color: var(--rv-danger-ink);
}

.AttachmentChipError > svg {
  color: var(--rv-danger);
}

.AttachmentName {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.AttachmentMeta {
  flex: 0 0 auto;
  color: var(--rv-fg-3);
}

.AttachmentChipError .AttachmentMeta {
  color: var(--rv-danger-ink);
}

.AttachmentRemove {
  flex: 0 0 auto;
  width: var(--rv-icon-md);
  height: var(--rv-icon-md);
  min-height: 0;
  padding: 0;
  border-radius: var(--rv-radius-full);
}

.AttachmentRemove svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.Thumb {
  position: relative;
  flex: 0 0 auto;
  width: var(--rv-space-16);
  height: var(--rv-space-16);
  overflow: hidden;
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
}

.ThumbUploading .ThumbImage {
  opacity: 50%;
}

.ThumbImage {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ThumbRemove {
  position: absolute;
  top: var(--rv-space-1);
  right: var(--rv-space-1);
  width: var(--rv-icon-md);
  height: var(--rv-icon-md);
  min-height: 0;
  padding: 0;
  background: var(--rv-surface);
  border-radius: var(--rv-radius-full);
}

.ThumbRemove svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

/* Sent images overlap like a fanned stack; each later one sits on top of the one before. */
.Stack {
  display: flex;
  justify-content: flex-end;
  padding: var(--rv-space-1);
}

.StackImage {
  flex: 0 0 auto;
  width: var(--rv-space-20);
  height: var(--rv-space-20);
  margin-left: calc(var(--rv-space-8) * -1);
  object-fit: cover;
  background: var(--rv-bg-app-subtle);
  border: 2px solid var(--rv-surface);
  border-radius: var(--rv-radius-lg);
  box-shadow: var(--rv-shadow-md);
  transition: transform 0.15s ease;
}

.StackImage:first-child {
  margin-left: 0;
}

.StackImage:nth-child(odd) {
  transform: rotate(-3deg);
}

.StackImage:nth-child(even) {
  transform: rotate(3deg);
}

.StackImage:hover {
  z-index: 1;
  transform: rotate(0deg) translateY(calc(var(--rv-space-1) * -1));
}

.AttachmentHint {
  margin: 0;
  padding: 0 var(--rv-space-1);
  font-size: var(--rv-text-xs);
  color: var(--rv-danger-ink);
}

.UserAttachments {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--rv-space-2);
  margin-bottom: var(--rv-space-2);
}

.UserAttachmentChip {
  display: inline-flex;
  align-items: center;
  gap: var(--rv-space-2);
  min-width: 0;
  max-width: 100%;
  padding: var(--rv-space-1) var(--rv-space-3);
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-2);
}

.UserAttachmentChip > svg {
  flex: 0 0 auto;
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
  color: var(--rv-fg-3);
}

.DropOverlay {
  position: absolute;
  inset: var(--rv-space-3);
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  align-items: center;
  justify-content: center;
  border: 2px dashed var(--rv-accent);
  border-radius: var(--rv-radius-2xl);
  background: var(--rv-surface);
  color: var(--rv-fg-2);
  font-size: var(--rv-text-md);
  font-weight: var(--rv-w-medium);

  /* Must not intercept the drag events the conversation listens for. */
  pointer-events: none;
}

.DropOverlay svg {
  width: var(--rv-icon-xl);
  height: var(--rv-icon-xl);
  color: var(--rv-accent);
}

/* ---------------- docked panel variant ---------------- */

/* The same conversation inside the bottom-right bubble: narrower, so less
   padding, one suggestion per row, and a shorter empty state. */
.ConversationPanel .Scroll {
  padding: var(--rv-space-3) var(--rv-space-4);
}

.ConversationPanel .Empty {
  padding-top: var(--rv-space-6);
}

.ConversationPanel .Suggestions {
  grid-template-columns: 1fr;
  margin-top: var(--rv-space-3);
}

.ConversationPanel .ComposerDock {
  padding: var(--rv-space-2) var(--rv-space-4) var(--rv-space-4);
}
